/**
 * Utilidades para manejo de fechas en zona horaria de Caracas
 * Zona horaria: America/Caracas (UTC-4)
 */

const CARACAS_TIMEZONE = 'America/Caracas';

/**
 * Formatea una fecha/hora en zona horaria de Caracas
 * @param {string|Date} fecha - Fecha en formato ISO o objeto Date
 * @param {object} options - Opciones de formato (opcional)
 * @returns {string} Fecha formateada
 */
export const formatearFechaCaracas = (fecha, options = {}) => {
  if (!fecha) return '';
  
  const date = typeof fecha === 'string' ? new Date(fecha) : fecha;
  
  const defaultOptions = {
    timeZone: CARACAS_TIMEZONE,
    ...options
  };
  
  return date.toLocaleString('es-ES', defaultOptions);
};

/**
 * Formatea solo la fecha (sin hora) en zona horaria de Caracas
 * @param {string|Date} fecha 
 * @returns {string} Fecha formateada (ej: "8 de diciembre de 2025")
 */
export const formatearSoloFechaCaracas = (fecha) => {
  return formatearFechaCaracas(fecha, {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

/**
 * Formatea solo la fecha corta en zona horaria de Caracas
 * @param {string|Date} fecha 
 * @returns {string} Fecha formateada (ej: "08/12/2025")
 */
export const formatearFechaCortaCaracas = (fecha) => {
  return formatearFechaCaracas(fecha, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
};

/**
 * Formatea solo la hora en zona horaria de Caracas
 * @param {string|Date} fecha 
 * @returns {string} Hora formateada (ej: "14:30")
 */
export const formatearHoraCaracas = (fecha) => {
  return formatearFechaCaracas(fecha, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
};

/**
 * Formatea fecha y hora completa en zona horaria de Caracas
 * @param {string|Date} fecha 
 * @returns {string} Fecha y hora formateada
 */
export const formatearFechaHoraCaracas = (fecha) => {
  return formatearFechaCaracas(fecha, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

/**
 * Formatea fecha con día de la semana en zona horaria de Caracas
 * @param {string|Date} fecha 
 * @returns {string} Fecha con día de semana (ej: "lunes, 8 de diciembre de 2025")
 */
export const formatearFechaConDiaCaracas = (fecha) => {
  return formatearFechaCaracas(fecha, {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

/**
 * Obtiene la fecha actual en zona horaria de Caracas
 * @returns {Date} Fecha actual
 */
export const obtenerFechaCaracas = () => {
  return new Date(new Date().toLocaleString('en-US', { timeZone: CARACAS_TIMEZONE }));
};

/**
 * Convierte una fecha string a objeto Date en zona horaria de Caracas
 * Para fechas sin hora (YYYY-MM-DD), agrega hora del mediodía para evitar problemas de timezone
 * @param {string} fechaStr - Fecha en formato ISO
 * @returns {Date} Objeto Date
 */
export const parsearFechaCaracas = (fechaStr) => {
  if (!fechaStr) return null;
  
  // Si es solo fecha (YYYY-MM-DD), agregar hora del mediodía
  if (fechaStr.length === 10 && fechaStr.includes('-')) {
    return new Date(fechaStr + 'T12:00:00');
  }
  
  return new Date(fechaStr);
};
